import * as pc from "playcanvas";

import type { Number4 } from "../types/Number";
import type { ReadonlyMatrix4 } from "./Matrix4";
import { isApproximately } from "./Number";
import type { ReadonlyVector3, Vector3 } from "./Vector3";

export interface ReadonlyQuaternion {
  get x(): number;
  get y(): number;
  get z(): number;
  get w(): number;

  clone(): Quaternion;
  equals(rhs: ReadonlyQuaternion): boolean;
  equalsApprox(rhs: ReadonlyQuaternion, epsilon?: number): boolean;
  getAxisAngle(axis: ReadonlyVector3): number;
  getEulerAngles(eulers?: ReadonlyVector3): Vector3;
  length(): number;
  lengthSq(): number;
  toString(): string;
  transformVector(vec: ReadonlyVector3, res?: Vector3): Vector3;
  toArray(): Number4;

  // Extensions
  isApproximately(rhs: ReadonlyQuaternion): boolean;

  // Convert for use in external JS code.
  // This is unsafe and should only be used when necessary.
  unsafeCast(): Quaternion;
}

declare module "playcanvas" {
  interface Quat extends ReadonlyQuaternion {
    x: number;
    y: number;
    z: number;
    w: number;

    clone(): Quaternion;
    conjugate(src?: this): this;
    copy(rhs: ReadonlyQuaternion): this;
    equals(rhs: ReadonlyQuaternion): boolean;
    equalsApprox(rhs: ReadonlyQuaternion, epsilon?: number): boolean;
    getAxisAngle(axis: Vector3): number;
    getEulerAngles(eulers?: Vector3): Vector3;
    invert(src?: ReadonlyQuaternion): this;
    length(): number;
    lengthSq(): number;
    mul(rhs: ReadonlyQuaternion): this;
    mul2(lhs: ReadonlyQuaternion, rhs: ReadonlyQuaternion): this;
    mulScalar(scalar: number, src?: this): this;
    normalize(src?: ReadonlyQuaternion): this;
    set(x: number, y: number, z: number, w: number): this;
    setFromAxisAngle(axis: ReadonlyVector3, angle: number): this;
    setFromDirections(from: ReadonlyVector3, to: ReadonlyVector3): this;
    setFromEulerAngles(ex: number | ReadonlyVector3, ey?: number, ez?: number): this;
    setFromMat4(m: ReadonlyMatrix4): this;
    slerp(lhs: ReadonlyQuaternion, rhs: ReadonlyQuaternion, alpha: number): this;
    toString(): string;
    transformVector(vec: ReadonlyVector3, res?: ReadonlyVector3): Vector3;
    toArray(): Number4;
    copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;

    // Extensions
    fromArray(array: Readonly<Number4>): this;
    recycle(): void;
  }
}

pc.Quat.prototype.fromArray = function (array: Readonly<Number4>) {
  this.x = array[0];
  this.y = array[1];
  this.z = array[2];
  this.w = array[3];
  return this;
};

pc.Quat.prototype.isApproximately = function (rhs) {
  return isApproximately(this.x, rhs.x) && isApproximately(this.y, rhs.y) && isApproximately(this.z, rhs.z);
};

pc.Quat.prototype.recycle = function () {
  pool.push(this);
};

pc.Quat.prototype.unsafeCast = function () {
  return this;
};

const pool: Quaternion[] = [];

// @ts-expect-error - Override static constants type (intentionally incompatible with base class)
export class Quaternion extends pc.Quat {
  static isInstance(value: unknown) {
    return value instanceof pc.Quat;
  }

  static override IDENTITY = Quaternion.makeReadonly(0, 0, 0, 1);
  static override ZERO = Quaternion.makeReadonly(0, 0, 0, 0);

  /**
   * @summary Creates a quaternion from a variety of input types.
   */
  static from(json: unknown): Quaternion {
    if (Quaternion.isInstance(json)) {
      return json.clone();
    } else if (
      Array.isArray(json) &&
      json.length >= 4 &&
      typeof json[0] === "number" &&
      typeof json[1] === "number" &&
      typeof json[2] === "number" &&
      typeof json[3] === "number"
    ) {
      return this.make(json[0], json[1], json[2], json[3]);
    } else if (
      typeof json === "object" &&
      json !== null &&
      "x" in json &&
      "y" in json &&
      "z" in json &&
      "w" in json &&
      typeof json.x === "number" &&
      typeof json.y === "number" &&
      typeof json.z === "number" &&
      typeof json.w === "number"
    ) {
      return this.make(json.x, json.y, json.z, json.w);
    }

    throw new Error("Invalid input for Quaternion.from");
  }

  /**
   * @summary Creates a new vector with the given components.
   */
  static make(x = 0, y = 0, z = 0, w = 1): Quaternion {
    return pool.pop()?.set(x, y, z, w) ?? new Quaternion(x, y, z, w);
  }

  /**
   * Create a readonly Quaternion that cannot be recycled.
   */
  static makeReadonly(x: number, y: number, z: number, w: number): ReadonlyQuaternion {
    return this.make(x, y, z, w);
  }

  /**
   * @summary Creates a new uninitialized quaternion.
   */
  static makeUninitialized(): Quaternion {
    return pool.pop() ?? new Quaternion();
  }
}
